/* eslint no-console: 0 */

import { observable, action, makeObservable, toJS, computed } from 'mobx'
import { lens } from 'lorgnette'
import * as R from 'ramda'
import { fromServerToClient, isEmpty, isPresent } from '../../../../lib/utils/collection'
import { toPolygonName } from '../../../../lib/dataMappings/search'
import { listingCaptchaKey } from '../../../../lib/config/recaptcha'
import { SRP_FORM_VALUE_CHANNEL } from '../../../../lib/constants/channels'
import { fetchFromSessionStorage } from '../../../../lib/utils/browserStorage'
import { VIEWED_LISTING_ID } from '../../../../lib/constants/storageKeys'
import { SRP_SEARCH_INFO, SRP_SEARCH_VALUES_SCOPE } from '../../../../lib/constants/search'
import { rootStoreInstance } from '../../../Srp/SearchBar/stores'
import { getMapBoundsByPathName } from '../../../../lib/utils/mapBounds'
import { SPECIAL_AREA } from '../../../Srp/SearchBar/stores/ValueStateStore'

const activeItemLens = listing => lens.firstOf(l => l.id === listing.id)

class ListingsMapStore {
  @observable collection = []
  @observable mapCollectionMeta = null
  @observable activeItem = null
  @observable hoveredListingId = null
  @observable highlightedAreas = []
  @observable isLoadingListing = false

  isAllowedRequest = true

  constructor({ transport, channel, eventBus, sessionStorage }) {
    makeObservable(this)

    this.transport = transport
    this.channel = channel
    this.sessionStorage = sessionStorage

    this.channel.init(SRP_FORM_VALUE_CHANNEL)

    eventBus.addEventListener('transport.Listing.updateSavedStatus', this.updateListingSaveStatus)
    eventBus.addEventListener('transport.Listing.updateSavedStatusTask', this.updateListingSaveStatus)
    eventBus.addEventListener('send-contact', this.setListingSavedStatus)
    eventBus.addEventListener('fetch-listings', this.fetchCollection)
  }

  @action('[ListingsMapStore] Set Initial State')
  setInitialState(props) {
    this.header = props.header
    this.collection = fromServerToClient(props).collection
    this.hoveredListingId = null
  }

  @action('[ListingsMapStore] reset store')
  resetStore() {
    this.collection = []
    this.mapCollectionMeta = null
    this.activeItem = null
    this.hoveredListingId = null
    this.highlightedAreas = []
    this.isLoadingListing = false
  }

  updateHighlightedAreas = values => {
    const hasFullTownNames = isPresent(values.fullTownNames) && values.fullTownNames.length > 0
    const hasTownNames = isPresent(values.townNames) && values.townNames.length > 0
    const hasSpecialArea = values.amenityNames && values.amenityNames.includes(SPECIAL_AREA)

    if (hasFullTownNames) {
      this.setHighlightedAreas(values.fullTownNames.map(toPolygonName))
    } else if (hasTownNames) {
      this.setHighlightedAreas(values.townNames.map(toPolygonName))
    } else if (hasSpecialArea) {
      this.setHighlightedAreas([SPECIAL_AREA])
    } else {
      this.setHighlightedAreas([])
    }
  }

  @action markAsActive(listing) {
    this.activeItem = listing
  }

  @action
  markAsNoActive = () => {
    this.activeItem = null
  }

  @action updateCollection(listings) {
    this.collection = listings
  }

  @action setHoveredListingId = listingId => {
    this.hoveredListingId = parseInt(listingId, 10)
  }

  @action('[ListingsMapStore] update listing viewed status')
  updateListingViewedStatus = () => {
    const viewedListings = fetchFromSessionStorage(VIEWED_LISTING_ID) || {}

    const updatedCollection = this.collection
      .map(listing => viewedListings[listing.id] ? { ...listing, viewed: true } : listing)

    this.updateCollection(updatedCollection)
  }

  @action('[ListingsMapStore] update listing save status')
  updateListingSaveStatus = ({ detail: { data } }) => {
    const listing = fromServerToClient(data)
    const activeItem = toJS(R.find(R.propEq('id', listing.id))(this.collection))
    const base = this.activeItem

    if (!base) { return }

    this.activeItem = {
      ...base,
      ...activeItem,
      links: {
        ...(activeItem.links || listing.links),
        saveAction: listing.links?.saveAction
      },
      saved: listing.saved
    }

    this.collection = activeItemLens(data).set(this.collection, this.activeItem)
  }

  @action('[ListingsMapStore] set listing saved status')
  setListingSavedStatus = ({ detail: { listingId } }) => {
    activeItemLens({ id: listingId })
      .get(this.collection)
      .then(listing => {
        if (listing.saved) {
          this.activeItem = listing
          return
        }

        this.updateSaveStatusTask(listing)
      })
  }

  @action('[ListingsMapStore] update listing save status')
  setHighlightedAreas = value => {
    this.highlightedAreas = value
  }

  @action('[ListingsMapStore] update listing loading')
  setListingLoading = value => {
    this.isLoadingListing = value
  }

  @action('[ListingsMapStore] set meta for map collection')
  setMapCollectionMeta = data => {
    this.mapCollectionMeta = data
  }

  @action('[ListingsMapStore] get listing details by Id')
  getListingDetails = (listing, filters, setLoading) => {
    setLoading(true)

    const listingType = listing.listingClass === 'Rental' ? 'rentals' : 'sales'
    const filtersToSendForRental = {}

    if (listingType === 'rentals') {
      if (filters.year) {filtersToSendForRental.year = filters.year}
      if (filters.pricePeriod) { filtersToSendForRental.pricePeriod = filters.pricePeriod}
      if (filters.order) { filtersToSendForRental.order = filters.order}
    }

    this.transport.Listing
      .getListingById(listingType, listing.id, filtersToSendForRental)
      .then(response => {
        const listing = fromServerToClient(response.data)

        this.markAsActive(listing)
      })
      .catch(error => { console.log(error) })
      .finally(() => {
        setLoading(false)
      })
  }

  @action('[ListingsMapStore] search collection')
  fetchCollection = async ({ isFirstFetch = false }) => {
    try {
      if (this.isLoadingListing) { return }
      this.setListingLoading(true)

      const {
        listingTypeStore: { current, listingTypes },
        valueStateStore: { valuesForSubmit, setTownChanged }
      } = rootStoreInstance

      this.updateHighlightedAreas(valuesForSubmit)

      const mapBounds = getMapBoundsByPathName(window.location.pathname)
      if (isFirstFetch && !isEmpty(mapBounds)) {
        valuesForSubmit.mapBounds = mapBounds
      }

      const searchValuesScope = this.sessionStorage.fetchFromSessionStorage(SRP_SEARCH_VALUES_SCOPE)
      const searchInfo = this.sessionStorage.fetchFromSessionStorage(SRP_SEARCH_INFO)
      const query = { [searchValuesScope]: valuesForSubmit, page: { size: 500 }, seed: searchInfo.seed }

      const response = await this.transport.Listing.sendMapSearch(listingTypes[current].mapSearchUrl, query)
      const { data, meta } = fromServerToClient(response)

      this.setMapCollectionMeta(meta)
      this.updateCollection(data)
      this.updateListingViewedStatus()
      this.setListingLoading(false)

      const hasSpecialAreaAmenity = valuesForSubmit.amenityNames && valuesForSubmit.amenityNames.includes(SPECIAL_AREA)

      if ((valuesForSubmit.townNames?.length > 0) || hasSpecialAreaAmenity) {
        setTownChanged(true)
      }
    } catch (e) {
      this.setListingLoading(false)
    }
  }

  @computed
  get activeItemID() {
    return this.activeItem && this.activeItem.id
  }

  updateSaveStatus = listing => {
    const { links: { saveAction: { url, method } } } = listing
    if (this.isAllowedRequest) {
      this.isAllowedRequest = false
      this.transport.Listing.updateSavedStatus({ url, method, captchaKey: listingCaptchaKey(listing) })
        .then(() => (this.isAllowedRequest = true))
        .catch(error => { console.log(error) })
    }
  }

  updateSaveStatusTask = listing => {
    const { links: { saveAction: { url, method } } } = listing
    if (this.isAllowedRequest) {
      this.isAllowedRequest = false
      this.transport.Listing.updateSavedStatusTask({ url, method, captchaKey: listingCaptchaKey(listing) })
        .then(() => { this.isAllowedRequest = true })
        .catch(error => { console.log(error) })
    }
  }
}

export default ListingsMapStore
